import { CurrencyPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import {
  FullRouterStateSerializer,
  provideRouterStore,
  RouterStateSerializer
} from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { DeviceDetectorService } from 'ngx-device-detector';
import { provideToastr } from 'ngx-toastr';

import * as AutoPayEffects from '@amfam/billing/auto-pay/data-access/lib/+state/auto-pay.effects';
import {
  autoPayFeatureKey,
  reducers as autoPayReducers
} from '@amfam/billing/auto-pay/data-access/lib/+state/auto-pay.selectors';
import * as BillAccountCanRegisterEffects from '@amfam/billing/bill-account-can-register/data-access/lib/+state/bill-account-can-register.effects';
import { billAccountCanRegisterReducer } from '@amfam/billing/bill-account-can-register/data-access/lib/+state/bill-account-can-register.reducer';
import { BILL_ACCOUNT_CAN_REGISTER_FEATURE_KEY } from '@amfam/billing/bill-account-can-register/data-access/lib/+state/bill-account-can-register.selectors';
import * as BillingEffects from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.effects';
import { BILLING_FEATURE_KEY } from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.reducer';
import { billingReducers } from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.selectors';
import * as BillingExpDocumentsEffects from '@amfam/billing/billaccount/data-access/lib/+state/billaccountDocuments/billaccountExpDocuments.effects';
import { BILLING_EXP_DOCUMENTS_FEATURE_KEY } from '@amfam/billing/billaccount/data-access/lib/+state/billaccountDocuments/billaccountExpDocuments.reducer';
import { billingExpDocumentsReducers } from '@amfam/billing/billaccount/data-access/lib/+state/billaccountDocuments/billaccountExpDocuments.selectors';
import * as BillingExpEffects from '@amfam/billing/billaccount/data-access/lib/+state/billaccountExp.effects';
import { BILLING_EXP_FEATURE_KEY } from '@amfam/billing/billaccount/data-access/lib/+state/billaccountExp.reducer';
import { billingExpReducers } from '@amfam/billing/billaccount/data-access/lib/+state/billaccountExp.selectors';
import { BillAccountsService } from '@amfam/billing/billaccount/data-access/lib/services/bill-accounts.service';
import { BillingUtilService } from '@amfam/billing/billaccount/data-access/lib/services/billing-util.service';
import * as OneTimePayFeatureEffects from '@amfam/billing/one-time-pay/feature/lib/+state/one-time-pay-feature.effects';
import {
  oneTimePayFeatureKey,
  oneTimePayFeatureReducer
} from '@amfam/billing/one-time-pay/feature/lib/+state/one-time-pay-feature.reducer';
import * as PaymentHistoryEffects from '@amfam/billing/payment-history/data-access/lib/+state/payment-history.effects';
import {
  PAYMENT_HISTORY_FEATURE_KEY,
  paymentHistoryReducer
} from '@amfam/billing/payment-history/data-access/lib/+state/payment-history.reducer';
import {
  expPaymentMethodFeatureKey,
  expReducers,
  paymentMethodFeatureKey,
  paymentMethodReducers
} from '@amfam/billing/payment-method/data-access/lib/+state';
import * as FinancialAccountEffects from '@amfam/billing/payment-method/data-access/lib/+state/financial-account/financial-account.effects';
import * as PaymentMethodExpEffects from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method-exp.effects';
import * as PaymentMethodEffects from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.effects';
import {
  expPaymentMethodExpFeatureKey,
  paymentMethodExpFeatureReducer
} from '@amfam/billing/payment-method/feature/lib/+state/payment-method-exp-feature.reducer';
import {
  paymentMethodFeatureReducer,
  paymentMethodSetupFeatureKey
} from '@amfam/billing/payment-method/feature/lib/+state/payment-method-feature.reducer';
import * as PaymentAccountEffects from '@amfam/billing/paymentaccount/data-access/lib/+state/paymentaccount.effects';
import { PAYMENT_ACCOUNT_FEATURE_KEY } from '@amfam/billing/paymentaccount/data-access/lib/+state/paymentaccount.reducer';
import { paymentAccountReducers } from '@amfam/billing/paymentaccount/data-access/lib/+state/paymentaccount.selectors';
import * as PaymentConfirmationEffects from '@amfam/billing/paymentconfirmation/data-access/lib/+state/paymentConfirmation.effects';
import {
  PAYMENT_CONFIRMATION_FEATURE_KEY,
  paymentConfirmationReducers
} from '@amfam/billing/paymentconfirmation/data-access/lib/+state/paymentConfirmation.selectors';
import { pendingPaymentReducer } from '@amfam/billing/pending-payment/data-access/lib/+state/pending-payment.reducer';
import { billingRegistrationReducers } from '@amfam/billing/registration/data-access/lib/+state/registration.selectors';
import * as ScheduledPaymentEffects from '@amfam/billing/schedulepayment/data-access/lib/+state/schedulepayment.effects';
import { SCHEDULE_PAYMENT_FEATURE_KEY } from '@amfam/billing/schedulepayment/data-access/lib/+state/schedulepayment.reducer';
import { schedulePaymentReducers } from '@amfam/billing/schedulepayment/data-access/lib/+state/schedulepayment.selectors';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util/lib/services/bill-account-transmute.service';
import { PaymentAccountTransmuteService } from '@amfam/billing/shared/util/lib/services/payment-account-transmute.service';
import { PaymentService } from '@amfam/billing/shared/util/lib/services/payment.service';
import * as ClaimV2DashboardEffects from '@amfam/claim/dashboardV2/data-access/lib/+state/claim-v2-dashboard.effects';
import {
  CLAIM_V2_DASHBOARD_FEATURE_KEY,
  claimV2DashboardReducer
} from '@amfam/claim/dashboardV2/data-access/lib/+state/claim-v2-dashboard.reducer';
import { ClaimsV2Service } from '@amfam/claim/data-access/lib/services/claimv2.service';
import * as ClaimFaqEffects from '@amfam/claim/faq/data-access/lib/+state/claim-faq.effects';
import {
  CLAIMFAQ_FEATURE_KEY,
  initialState as claimFaqInitialState,
  claimFaqReducer
} from '@amfam/claim/faq/data-access/lib/+state/claim-faq.reducer';
import { env } from '@amfam/environment';
import {
  paperlessEnrollmentFeatureKey,
  paperlessEnrollmentReducer
} from '@amfam/overview/paperless-enrollment/feature/lib/+state/overview-paperless-enrollment-feature-reducer';
import * as DocumentsEffects from '@amfam/policy/data-access/lib/+state/documents/documents.effects';
import * as PoliciesEffects from '@amfam/policy/data-access/lib/+state/policies/policies.effects';
import { POLICIES_FEATURE_KEY } from '@amfam/policy/data-access/lib/+state/policies/policies.reducer';
import { policiesReducers } from '@amfam/policy/data-access/lib/+state/policies/policies.selectors';
import * as SummariesEffects from '@amfam/policy/data-access/lib/+state/summaries/summaries.effects';
import * as ESignatureEffects from '@amfam/policy/e-signature/data-access/lib/+state/e-signature.effects';
import { ESIGNATURE_FEATURE_KEY } from '@amfam/policy/e-signature/data-access/lib/+state/e-signature.reducer';
import { esignatureReducers } from '@amfam/policy/e-signature/data-access/lib/+state/e-signature.selectors';
import * as GoPaperlessEffects from '@amfam/policy/go-paperless/data-access/lib/+state/go-paperless.effects';
import {
  GO_PAPERLESS_FEATURE_KEY,
  goPaperLessReducers
} from '@amfam/policy/go-paperless/data-access/lib/+state/go-paperless.selectors';
import * as VehicleEffects from '@amfam/policy/vehicle/data-access/lib/+state/vehicle.effects';
import { vehicleReducer } from '@amfam/policy/vehicle/data-access/lib/+state/vehicle.reducer';
import * as DeleteEffects from '@amfam/profile/data-access/lib/+state/delete-myaccount-state/delete-myaccount.effect';
import { deleteMyAccountReducer } from '@amfam/profile/data-access/lib/+state/delete-myaccount-state/delete-myaccount.reducer';
import * as ProfileEffects from '@amfam/profile/data-access/lib/+state/profile.effects';
import {
  PROFILE_FEATURE_KEY,
  profileReducer
} from '@amfam/profile/data-access/lib/+state/profile.reducer';
import { AlternateAuthService } from '@amfam/profile/data-access/lib/services/alternate-auth.service';
import { DeleteService } from '@amfam/profile/data-access/lib/services/delete.service';
import { DigitalServiceProgramService } from '@amfam/profile/data-access/lib/services/digital-service-program.service';
import { PartyService } from '@amfam/profile/data-access/lib/services/party.service';
import { ProfileUtilService } from '@amfam/profile/data-access/lib/services/profile-util.service';
import * as PreferencesEffects from '@amfam/profile/user-account-preferences/data-access/lib/data-access/+state/preferences.effects';
import * as UserAccountPreferencesEffects from '@amfam/profile/user-account-preferences/data-access/lib/data-access/+state/preferences.effects';
import {
  preferencesFeature,
  preferencesFeatureKey,
  preferencesReducer
} from '@amfam/profile/user-account-preferences/data-access/lib/data-access/+state/preferences.reducer';
import * as AnalyticsEffects from '@amfam/shared/analytics/lib/+state/analytics.effects';
import {
  analyticsFeatureKey,
  analyticsReducer
} from '@amfam/shared/analytics/lib/+state/analytics.reducer';
import {
  BROWSER_FAVICONS_CONFIG,
  BrowserFavicons,
  DEFAULT_FAVICONS_CONFIG,
  FaviconService
} from '@amfam/shared/favicon/lib/services/favicon.service';
import { getAddVehicleRenewalRate } from '@amfam/shared/renewal/lib/+state/renewal-rate.effect';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes/lib/pipes/format.pipe';
import { USER_FEATURE_KEY, userReducer } from '@amfam/shared/user/lib/+state/user.reducer';
import * as AgentEffects from '@amfam/shared/utility/agent/lib/+state/agent.effects';
import {
  AGENT_FEATURE_KEY,
  agentReducer
} from '@amfam/shared/utility/agent/lib/+state/agent.reducer';
import * as brandMultiPartnerEffects from '@amfam/shared/utility/brand/lib/+state/brand-multiple-partner.effects';
import {
  BRAND_MULTIPARTNER_FEATURE_KEY,
  brandMultiPartnerReducer,
  initialStateMultiPartner
} from '@amfam/shared/utility/brand/lib/+state/brand-multiple-partner.reducer';
import {
  BRAND_FEATURE_KEY,
  brandReducer
} from '@amfam/shared/utility/brand/lib/+state/brand.reducer';
import { initialBrandModel as brandInitialState } from '@amfam/shared/utility/brand/lib/models/brand.model';
import { BrandService } from '@amfam/shared/utility/brand/lib/services/brand.service';
import { featureFlagReducer } from '@amfam/shared/utility/feature-flag/data-access/lib/+state/feature-flag.reducer';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import * as RouterEffects from '@amfam/shared/utility/navigation/lib/+state/router-effects';
import {
  CustomRouterStateSerializer,
  NAVIGATION_FEATURE_KEY,
  routerReducer
} from '@amfam/shared/utility/navigation/lib/+state/router-reducer';
import { NavigationService } from '@amfam/shared/utility/navigation/lib/services/navigation.service';
import { provideCopyInitializer } from '@amfam/shared/utility/shared-services/lib/copy/copy-initializer';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { WINDOW_PROVIDERS } from '@amfam/shared/utility/shared-services/lib/window/window.service';
import { ValidationService } from '@amfam/ui-kit/lib/form-controls/services/validation.service';
import { LoadingSpinnerService } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner.service';
import { PageTitleService } from '@amfam/ui-kit/lib/page-title/page-title.service';

import { appRoutingProviders } from './app.routes';
import { BusinessConversionService } from './business/conversion/business-conversion.service';
import { FeatureDetectionService } from './core/feature-detection/feature-detection.service';
import { httpInterceptorFns } from './core/http-interceptors';
import { metaReducers, reducers } from './core/store';
import * as UserNotificationsEffects from './core/store/alert/user-notifications.effects';
import * as AppBootstrapEffects from './core/store/app-bootstrap/app-bootstrap.effects';
import * as ReplaceVehicleEffects from './core/store/replace-vehicle/replace-vehicle.effects';
import { ReplaceVehicleService } from './core/store/replace-vehicle/replace-vehicle.service';
import * as ForgerockSessionEffects from './core/store/session/forgerock-session.effects';
import * as SessionEffects from './core/store/session/session.effects';
import * as WorkflowEffects from './core/store/workflow/workflow.effects';
import { WorkflowGuardService } from './core/workflow/workflow-guard.service';
import { WorkflowService } from './core/workflow/workflow.service';
import { ExitWarningService } from './enrollment/exit-warning/exit-warning.service';
import { ResetRoutesService } from './enrollment/personal/shared/personal-enrollment-guards/reset-routes.service';
import { ErrorProviders } from './error/error.routes';
import { SessionActivityService } from './login/session-activity/session-activity.service';
import { coverageChangeReducers, policyChangereducers } from './policy/+state';
import * as CoverageChangeEffects from './policy/+state/coverage-change.effects';
import * as PolicyChangeEffects from './policy/+state/policy-change.effects';
import { PreloadSelectedModules } from './selective-preload-strategy';

const VehicleRenewalEffects = { getAddVehicleRenewalRate };

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideCopyInitializer(),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      closeButton: false
    }),
    importProvidersFrom(NgxDatatableModule),
    provideHttpClient(withXhr(), withInterceptors(httpInterceptorFns)),
    ...WINDOW_PROVIDERS,
    ...ErrorProviders,
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: env.production
        ? { strictStateImmutability: true, strictActionImmutability: true }
        : { strictStateImmutability: false, strictActionImmutability: false }
    }),
    provideState(analyticsFeatureKey, analyticsReducer),
    provideEffects(AnalyticsEffects),
    provideState(USER_FEATURE_KEY, userReducer),
    {
      provide: FaviconService,
      useClass: BrowserFavicons
    },
    {
      provide: BROWSER_FAVICONS_CONFIG,
      useValue: DEFAULT_FAVICONS_CONFIG
    },
    provideState(preferencesFeature),
    provideState(paperlessEnrollmentFeatureKey, paperlessEnrollmentReducer),
    provideState(NAVIGATION_FEATURE_KEY, routerReducer),
    provideEffects(RouterEffects),
    provideState(AGENT_FEATURE_KEY, agentReducer),
    provideEffects(AgentEffects),
    provideState(BRAND_FEATURE_KEY, brandReducer, {
      initialState: brandInitialState
    }),
    provideState(BRAND_MULTIPARTNER_FEATURE_KEY, brandMultiPartnerReducer, {
      initialState: initialStateMultiPartner
    }),
    provideEffects(brandMultiPartnerEffects),
    provideState('featureFlag', featureFlagReducer),
    provideState('vehicle', vehicleReducer),
    provideEffects(VehicleEffects),
    provideState(PROFILE_FEATURE_KEY, profileReducer),
    provideState('delete-myaccount', deleteMyAccountReducer),
    provideState(preferencesFeatureKey, preferencesReducer),
    provideEffects(PreferencesEffects),
    provideEffects(ProfileEffects),
    provideEffects(DeleteEffects),
    PartyService,
    DigitalServiceProgramService,
    ProfileUtilService,
    AlternateAuthService,
    DeleteService,
    provideEffects(AppBootstrapEffects),
    provideEffects(ReplaceVehicleEffects),
    provideEffects(SessionEffects),
    provideEffects(ForgerockSessionEffects),
    provideEffects(UserNotificationsEffects),
    provideEffects(WorkflowEffects),
    provideEffects(VehicleRenewalEffects),
    provideEffects(UserAccountPreferencesEffects),
    provideState('policyChange', policyChangereducers),
    provideState('coverageChange', coverageChangeReducers),
    provideEffects(PolicyChangeEffects, CoverageChangeEffects),
    provideState(paymentMethodFeatureKey, paymentMethodReducers),
    provideState(expPaymentMethodFeatureKey, expReducers),
    provideEffects([PaymentMethodEffects, FinancialAccountEffects, PaymentMethodExpEffects]),
    provideState('pendingPayment', pendingPaymentReducer),
    provideState(BILL_ACCOUNT_CAN_REGISTER_FEATURE_KEY, billAccountCanRegisterReducer),
    provideEffects(BillAccountCanRegisterEffects),
    provideState(BILLING_FEATURE_KEY, billingReducers),
    provideState(BILLING_EXP_FEATURE_KEY, billingExpReducers),
    provideState(BILLING_EXP_DOCUMENTS_FEATURE_KEY, billingExpDocumentsReducers),
    provideState(autoPayFeatureKey, autoPayReducers),
    provideEffects(BillingEffects),
    provideEffects(BillingExpEffects),
    provideEffects(BillingExpDocumentsEffects),
    provideEffects(AutoPayEffects),
    BillAccountsService,
    BillingUtilService,
    provideState(PAYMENT_HISTORY_FEATURE_KEY, paymentHistoryReducer),
    provideEffects(PaymentHistoryEffects),
    provideState(PAYMENT_ACCOUNT_FEATURE_KEY, paymentAccountReducers),
    provideEffects(PaymentAccountEffects),
    provideState(PAYMENT_CONFIRMATION_FEATURE_KEY, paymentConfirmationReducers),
    provideEffects(PaymentConfirmationEffects),
    provideState('billing registrations', billingRegistrationReducers),
    provideState(SCHEDULE_PAYMENT_FEATURE_KEY, schedulePaymentReducers),
    provideEffects(ScheduledPaymentEffects),
    provideState(CLAIMFAQ_FEATURE_KEY, claimFaqReducer, { initialState: claimFaqInitialState }),
    provideEffects(ClaimFaqEffects),
    provideState(CLAIM_V2_DASHBOARD_FEATURE_KEY, claimV2DashboardReducer),
    provideEffects(ClaimV2DashboardEffects),
    ClaimsV2Service,
    provideState(POLICIES_FEATURE_KEY, policiesReducers),
    provideEffects(PoliciesEffects, SummariesEffects, DocumentsEffects),
    provideState(ESIGNATURE_FEATURE_KEY, esignatureReducers),
    provideEffects(ESignatureEffects),
    provideState(GO_PAPERLESS_FEATURE_KEY, goPaperLessReducers),
    provideEffects(GoPaperlessEffects),
    provideState(oneTimePayFeatureKey, oneTimePayFeatureReducer),
    provideEffects(OneTimePayFeatureEffects),
    provideState(paymentMethodSetupFeatureKey, paymentMethodFeatureReducer),
    provideState(expPaymentMethodExpFeatureKey, paymentMethodExpFeatureReducer),
    provideRouterStore({
      serializer: FullRouterStateSerializer,
      stateKey: 'router'
    }),
    ...(!env.production ? [provideStoreDevtools({ maxAge: 100, connectInZone: true })] : []),
    PreloadSelectedModules,
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    BrandService,
    BusinessConversionService,
    CopyService,
    CurrencyPipe,
    ExitWarningService,
    FeatureDetectionService,
    FeatureFlagService,
    LoadingSpinnerService,
    NavigationService,
    PageTitleService,
    PaymentAccountTransmuteService,
    PaymentService,
    BillAccountTransmuteService,
    ReplaceVehicleService,
    ResetRoutesService,
    UtilService,
    ValidationService,
    WorkflowGuardService,
    WorkflowService,
    DeviceDetectorService,
    SessionActivityService,
    CurrencyUSDPipe,
    ...appRoutingProviders
  ]
};
